import { useState } from 'react'
import { PieImage } from 'components/common'
import { ContentModal } from 'components/organisms/modals/content-modal'
import { Button } from 'components/atoms/button'

interface ContestWinnerModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	firstName?: string | null
	contestName: string
	eventAvatarUrl?: string | null
	onClose?: () => void
}

// ENG-2685: shown to a contest entrant once they are selected as a winner. This modal is
// rendered by the modal manager, which sits ABOVE the <Router> in the provider tree — so it
// must not use react-router hooks (useNavigate). It always opens on the event page itself, so
// "View my pass" just closes the modal to reveal the event page (where the pass lives) beneath.
export function ContestWinnerModal({
	open,
	setOpen,
	firstName,
	contestName,
	eventAvatarUrl,
	onClose,
}: ContestWinnerModalProps) {
	const handleClose = () => {
		setOpen(false)
		onClose?.()
	}

	const greetingName = firstName ? `, ${firstName}` : ''

	return (
		<ContentModal open={open} setOpen={setOpen} isModal hideCloseButton={true} title="You've been selected">
			<div className="relative flex flex-col items-center space-y-5">
				<div className="max-w-[304px]">
					<div className="flex justify-center items-center py-8">
						<PieImage className="w-[200px] h-auto rounded-xl flex-shrink-0" images={[eventAvatarUrl]} />
					</div>
					<span>
						Congratulations{greetingName}. You've been selected for {contestName}. Your pass is in your
						account now. Claim it before your claim window closes, you can see the deadline in your
						account. If it isn't claimed in time, it passes to the next person.
					</span>
				</div>
				<Button variant="primary" fullWidth onClick={handleClose}>
					View my pass
				</Button>
			</div>
		</ContentModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface ContestWinnerModalManagedProps {
	firstName?: string | null
	contestName: string
	eventAvatarUrl?: string | null
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'contest-winner': ContestWinnerModalManagedProps
	}
}

export function ManagedContestWinnerModal({
	onClose,
	...rest
}: ContestWinnerModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<ContestWinnerModal
			open={open}
			setOpen={(v: boolean) => {
				if (!v) handleClose()
			}}
			onClose={handleClose}
			{...rest}
		/>
	)
}
